import { SlateUtils } from '@all-hands-hq/plex-graphql-schema';
import { DataProxy } from '@apollo/client';
import { CircularProgress } from '@material-ui/core';
import { FetchResult } from 'apollo-link';
import React, { useState } from 'react';

import {
  AfterUpsertAnswerAction,
  CreateAnswerMutation,
  QuestionDetailsFragment,
  useCreateAnswerMutation,
  useGetQuestionDetailsQuery,
} from '../../../../graphql/gen/types';
import { prependAnswerToQuestionDetailsCache } from '../../../../graphql/updates/prependAnswerToQuestionDetailsCache';
import { SlateEditorValue } from '../../../shared/slate/SlateEditor';
import { logAnalytics } from '../../shared/logAnalytics';
import { useDialogState } from '../useDialogState';
import { FactAnswerDialogContent, FactAnswerInput } from './FactAnswerDialogContent';

type Props = {
  factId: string;
};

export function CreateFactAnswerDialog({ factId }: Props): React.ReactElement {
  const [, setDialogState] = useDialogState();
  const [state, setState] = useState<FactAnswerInput>({
    body: SlateEditorValue.new(),
    entities: { expanded_link: [], mention: [] },
    primaryLinkId: null,
  });
  // TODO: error handling
  const { data, loading } = useGetQuestionDetailsQuery({ variables: { questionId: factId } });
  // TODO: error handling
  const [createAnswerMutation, { loading: createLoading }] = useCreateAnswerMutation();
  const fileUploading = SlateUtils.hasUploadsInProgress(state.body.value);

  const fact: QuestionDetailsFragment | null | undefined = data?.loggedIn?.questionById;

  const handleClose = (): void => {
    setDialogState(null);
  };

  const handleSave = (afterAction: AfterUpsertAnswerAction): void => {
    createAnswerMutation({
      variables: {
        answer: {
          questionId: factId,
          body: state.body.value,
          primaryLinkId: state.primaryLinkId,
          afterAction,
        },
      },
      update: (proxy: DataProxy, { data }: FetchResult<CreateAnswerMutation>): void => {
        // Update cache on success
        const newAnswer = data?.createAnswer;
        if (newAnswer != null) {
          prependAnswerToQuestionDetailsCache(proxy, { questionId: factId, answer: newAnswer });
        }
      },
    }).then(({ data }) => {
      if (data?.createAnswer != null) {
        handleClose();
        logAnalytics({ name: 'answer created', details: { itemId: factId, plexId: fact?.plex.id } });
      }
    });
  };

  const handleChange = (state: FactAnswerInput): void => {
    setState(state);
  };

  if (!fact) {
    return <CircularProgress size={24} />;
  }

  return (
    <FactAnswerDialogContent
      mode="create"
      isApprovedAnswer={false}
      value={state}
      question={fact}
      onChange={handleChange}
      actionButtonOnClick={handleSave}
      actionButtonDisabled={loading || fileUploading || createLoading}
    />
  );
}
